import { useCallback, useState } from "react";
import type { ThemeMode } from "@xinghai/ui";

const KEY = "xz.theme";
const MODES: ThemeMode[] = ["system", "light", "dark"];

export const THEME_LABELS: Record<ThemeMode, string> = {
  system: "跟随系统",
  light: "浅色",
  dark: "深色",
};

function read(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    return v && (MODES as string[]).includes(v) ? (v as ThemeMode) : "system";
  } catch {
    return "system";
  }
}

/**
 * Theme preference. The design system switches palettes via
 * `body[data-ds-dark-theme]`, applied by `AppShell` / `useDesignSystemTheme`.
 */
export function useTheme(): {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  cycle: () => void;
} {
  const [theme, setThemeState] = useState<ThemeMode>(() => read());

  const setTheme = useCallback((mode: ThemeMode) => {
    setThemeState(mode);
    try {
      localStorage.setItem(KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const cycle = useCallback(() => {
    setThemeState((prev) => {
      const next = MODES[(MODES.indexOf(prev) + 1) % MODES.length] ?? "system";
      try {
        localStorage.setItem(KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return { theme, setTheme, cycle };
}

export type { ThemeMode };
